import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TerminologyService } from '@app/services/terminology/terminology.service';
import { Subscription } from 'rxjs';

/**
 * Renders the licence-configured value for a terminology key, falling back to
 * the canonical Evide default when no override is set. Usage:
 *
 *   {{ 'programme_singular' | term }}
 *   {{ 'programme_plural'   | term }}
 *
 * Impure so the rendered value updates when the admin saves new overrides
 * mid-session — the service refreshes its cache and the pipe re-evaluates on
 * the next change-detection tick. The subscription lives for the lifetime of
 * the pipe instance and is cleaned up via OnDestroy.
 */
@Pipe({
  name: 'term',
  pure: false,
  standalone: true,
})
export class TermPipe implements PipeTransform, OnDestroy {
  private lastKey: string | null = null;
  private lastValue = '';
  private sub?: Subscription;

  constructor(
    private terminology: TerminologyService,
    private cdr: ChangeDetectorRef,
  ) {}

  transform(termKey: string): string {
    if (!termKey) return '';
    if (termKey !== this.lastKey) {
      this.lastKey = termKey;
      this.sub?.unsubscribe();
      // Subscribe once per key change. The service emits whenever the cache
      // updates (load() / saveOverrides()), at which point we cache the new
      // value and ask Angular to redraw whatever's bound to this pipe.
      this.sub = this.terminology.get$(termKey).subscribe((value) => {
        this.lastValue = value;
        this.cdr.markForCheck();
      });
    }
    return this.lastValue;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
